// Optional auth middleware: parses the bearer token if present and attaches
// userId/supabase to context, but never throws when the user is unauthenticated.
// Use for endpoints that have meaningful behavior for both signed-in and anonymous callers.
import { createMiddleware } from "@tanstack/react-start";
import { getRequest } from "@tanstack/react-start/server";
import { createClient, type SupabaseClient } from "@supabase/supabase-js";
import type { Database } from "./types";

type OptionalAuthContext = {
  userId: string | null;
  supabase: SupabaseClient<Database> | null;
};

export const optionalSupabaseAuth = createMiddleware({ type: "function" }).server(
  async ({ next }) => {
    const empty: OptionalAuthContext = { userId: null, supabase: null };

    const SUPABASE_URL = process.env.SUPABASE_URL;
    const SUPABASE_PUBLISHABLE_KEY = process.env.SUPABASE_PUBLISHABLE_KEY;
    if (!SUPABASE_URL || !SUPABASE_PUBLISHABLE_KEY) {
      return next({ context: empty });
    }

    const request = getRequest();
    const authHeader = request?.headers?.get("authorization");
    if (!authHeader?.startsWith("Bearer ")) {
      return next({ context: empty });
    }

    const token = authHeader.replace("Bearer ", "");
    if (!token) return next({ context: empty });

    const supabase = createClient<Database>(SUPABASE_URL, SUPABASE_PUBLISHABLE_KEY, {
      global: { headers: { Authorization: `Bearer ${token}` } },
      auth: { storage: undefined, persistSession: false, autoRefreshToken: false },
    });

    const { data, error } = await supabase.auth.getClaims(token);
    if (error || !data?.claims?.sub) {
      return next({ context: empty });
    }

    const ctx: OptionalAuthContext = { supabase, userId: data.claims.sub as string };
    return next({ context: ctx });
  },
);
